import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { ChevronDownIcon } from '@/components/ui/icons';
import { useTranslation } from '@/i18n';
import styles from './SchedulePage.module.css';

/**
 * One Download button, two files behind it.
 *
 * A menu rather than two buttons, because the toolbar's job is the board and a
 * reader downloads rarely — one control says "this is where files come from", and
 * the choice of format is asked only once it matters. The two entries are named
 * with their extensions because that IS the difference: same filters, but the
 * spreadsheet is ten columns for a person and APCAS is the sixty-four-column
 * interchange file another system ingests.
 *
 * The open/close mechanics mirror RangePicker's, which is the app's dropdown:
 * outside pointerdown closes, Escape closes and returns focus, opening moves the
 * keyboard to the first option.
 *
 * The running download itself lives in the PARENT — this component only asks for
 * one. The trigger goes quiet while it runs: the menu closed on pick, so the
 * trigger is the only place the wait can be seen.
 */

export type ExportKind = 'excel' | 'apcas';

export const ExportMenu = ({
  busy,
  onPick,
}: {
  /** The export in flight, or null — disables the trigger and flips its label. */
  busy: ExportKind | null;
  onPick: (kind: ExportKind) => void;
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const boxRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const firstOptionRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setIsOpen(false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    firstOptionRef.current?.focus();

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  const pick = (kind: ExportKind) => {
    // Closed before the download starts, and focus back on the trigger — which is
    // about to say "Preparing…", so the reader's eye is already where the answer is.
    setIsOpen(false);
    triggerRef.current?.focus();
    onPick(kind);
  };

  const option = (kind: ExportKind, first: boolean) => (
    <button
      type="button"
      role="menuitem"
      ref={first ? firstOptionRef : undefined}
      className={styles.exportOption}
      onClick={() => pick(kind)}
    >
      {t(`schedule.export.${kind}`)}
    </button>
  );

  return (
    <div className={styles.export} ref={boxRef}>
      <Button
        size="sm"
        ref={triggerRef}
        onClick={() => setIsOpen((open) => !open)}
        disabled={busy !== null}
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        {busy !== null ? t('schedule.export.working') : t('schedule.export.label')}
        <ChevronDownIcon className={styles.exportCaret} />
      </Button>

      {isOpen && (
        <div className={styles.exportMenu} role="menu" aria-label={t('schedule.export.label')}>
          {option('excel', true)}
          {option('apcas', false)}
        </div>
      )}
    </div>
  );
};
